import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { SectionHead, Reveal } from "./shared";

const IMG = "https://images.unsplash.com/photo-1748609160056-7b95f30041f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHxfh81&q=85";

const BARS = [
  { m: "Jan", v: 96 }, { m: "Fev", v: 112 }, { m: "Mar", v: 121 },
  { m: "Abr", v: 138 }, { m: "Mai", v: 154 }, { m: "Jun", v: 162 },
];
const LINE = [
  { m: "S1", v: 38 }, { m: "S2", v: 42 }, { m: "S3", v: 41 }, { m: "S4", v: 46 }, { m: "S5", v: 49 }, { m: "S6", v: 48.6 },
];
const DONUT = [
  { name: "Moda feminina", value: 44 }, { name: "Moda masculina", value: 26 },
  { name: "Acessórios", value: 18 }, { name: "Calçados", value: 12 },
];
const COLORS = ["#2E7CF6", "#1A64FF", "#7DD3FC", "#3B5BA9"];

const tip = { contentStyle: { background: "rgba(6,13,31,0.95)", border: "1px solid rgba(125,211,252,0.25)", borderRadius: 10, fontSize: 11, fontFamily: "JetBrains Mono", color: "#fff" }, labelStyle: { color: "#7DD3FC" }, cursor: { stroke: "rgba(125,211,252,0.25)", fill: "rgba(125,211,252,0.06)" } };

export const VisualManagement = () => (
  <section id="gestao-visual" data-testid="visual-section" className="relative overflow-hidden py-24 sm:py-32">
    <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-tria-vivid/10 blur-[140px]" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead
        eyebrow="Gestão visual"
        testId="visual-head"
        title={<>Gestão feita por <span className="text-gradient-blue">indicadores, gráficos e visão de resultado</span></>}
        sub="A TRIA transforma gestão em leitura visual: o gestor enxerga em segundos o que está acontecendo no negócio."
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Reveal className="lg:row-span-2">
          <div data-testid="visual-photo" className="group relative h-full min-h-[420px] overflow-hidden rounded-2xl border border-white/[0.08]">
            <img src={IMG} alt="Gestora analisando painéis e indicadores da operação" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-tria-ink via-tria-ink/40 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-tria-sky">Na loja, todos os dias</span>
              <p className="mt-2 max-w-sm font-display text-xl font-semibold leading-snug text-white">
                Do cabide ao painel: cada peça vendida vira leitura de resultado.
              </p>
            </div>
            <div className="glass-card absolute right-5 top-5 rounded-xl px-4 py-3">
              <p className="text-[9.5px] font-medium text-tria-subtle">Ponto de equilíbrio</p>
              <p className="font-mono text-sm font-bold text-emerald-400">Atingido · dia 14</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-2">
          <div data-testid="visual-bar-chart" className="glass-card rounded-2xl p-5 sm:p-6">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-[12.5px] font-semibold text-white">Evolução mensal de receita realizada</p>
              <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 font-mono text-[10px] font-bold text-emerald-300">+68% no semestre</span>
            </div>
            <div className="h-[170px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={BARS} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                  <XAxis dataKey="m" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <Tooltip {...tip} formatter={(v) => [`R$ ${v} mil`, "Receita"]} />
                  <Bar dataKey="v" radius={[6, 6, 0, 0]}>
                    {BARS.map((_, i) => (
                      <Cell key={i} fill={i === BARS.length - 1 ? "#7DD3FC" : "#1A64FF"} fillOpacity={0.45 + i * 0.1} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.18}>
          <div data-testid="visual-line-chart" className="glass-card h-full rounded-2xl p-5 sm:p-6">
            <p className="mb-1 text-[12.5px] font-semibold text-white">Margem de contribuição semanal</p>
            <p className="font-mono text-2xl font-bold text-white">48,6%</p>
            <div className="mt-1 h-[110px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={LINE} margin={{ top: 8, right: 4, left: -28, bottom: 0 }}>
                  <XAxis dataKey="m" tick={{ fill: "#64748B", fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 9 }} axisLine={false} tickLine={false} unit="%" />
                  <Tooltip {...tip} formatter={(v) => [`${v}%`, "Margem"]} />
                  <Line type="monotone" dataKey="v" stroke="#7DD3FC" strokeWidth={2.5} dot={{ r: 2.5, fill: "#7DD3FC", strokeWidth: 0 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.26}>
          <div data-testid="visual-donut-chart" className="glass-card flex h-full items-center gap-4 rounded-2xl p-5 sm:p-6">
            <div className="relative h-[130px] w-[130px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={DONUT} innerRadius="60%" outerRadius="90%" paddingAngle={3} dataKey="value" stroke="none">
                    {DONUT.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Pie>
                  <Tooltip {...tip} formatter={(v, n) => [`${v}%`, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <span className="font-mono text-sm font-bold text-white">Mix</span>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-[12.5px] font-semibold text-white">Receita por categoria</p>
              {DONUT.map((d, i) => (
                <div key={d.name} className="flex items-center gap-2 text-[11px] text-tria-mist">
                  <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i] }} />
                  {d.name}
                  <span className="ml-auto font-mono font-semibold text-white">{d.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
